import { call, select, fork, race, take, put, takeLeading, delay } from 'redux-saga/effects';
import { clearBalancesCache } from 'data/accountBalances/balCache';
import { createJobStatus, deleteJobStatus, createJobStatusSuccess, createJobStatusFailure } from 'data/jobStatus/actions';
import { getSubscriptions } from 'data/subscriptions/subscriptionsActions';
import { getEntitlements } from 'data/entitlements/entitlementsActions';
import { getDatasets } from 'data/datasets/actions';
import { getInstitutionLogins } from 'data/institutionLogins/actions';
import { getAccounts, getInvestmentAccounts, refreshAccounts, refreshAccountsCompleted } from 'data/accounts/actions';
import { getTags } from 'data/tags/actions';
import { getCategories } from 'data/categories/actions';
import { getDocuments } from 'data/documents/actions';
import { getTransactions } from 'data/transactions/actions';
import { getPreferences } from 'data/preferences/actions';
import { getInvestmentHoldings } from 'data/investmentHoldings/actions';
import { getInvestmentBalancesV2 } from 'data/investmentBalancesV2/investmentBalancesV2Actions';
import { getInvestmentHoldingsV2 } from 'data/investmentHoldingsV2/investmentHoldingsV2Actions';
import { getFlagsSuccess, getFlagsFailure } from 'utils/featureFlags/actions';
import { initializeFeatureFlags } from 'utils/featureFlags/initialize';
import localStorageEx from 'utils/localStorageEx';
import { getDatasetId, isAuthTokenValid } from 'utils/auth/selectors';
import * as featureFlagsSelectors from 'utils/featureFlags/selectors';
import { getLogger } from 'utils/logger';
import { setAwaitingToAutoRefresh } from 'data/app/actions';
import { checkIfNPSSurveyRequestedAction } from 'data/nps/npsActions';
import { mcRulesExecute } from 'data/mcRules/mcRulesActions';
import { getBudgetsAction } from 'data/budgets/budgetsActions';
import { getBudgetItemsAction } from 'data/budgetItems/budgetItemsActions';
import { getCategoryGroupsAction } from 'data/categoryGroups/categoryGroupsActions';
import { getCategoryGroupListsAction } from 'data/categoryGroupLists/categoryGroupListsActions';
import { getScheduledTransactions } from 'data/scheduledTransactions/scheduledTransactionsActions';
import { getFreeToSpend } from 'data/freeToSpend/freeToSpendActions';
import { getFilters } from 'data/filters/actions';
import { getSpendingWatchListsInitial, getSpendingWatchLists } from 'data/spendingWatchList/actions';
import { getMemorizedRules } from 'data/memorizedRules/actions';
import { getRules } from 'data/renameRules/actions';
import { getAlerts } from 'data/alerts/alertsActions';

import * as actions from './actions';

const log = getLogger('data/app/sagas.js');

function *appRefreshAll(action) {
  log.info('refresh (start)->');

  const authTokenValid = yield select(isAuthTokenValid);
  if (authTokenValid) {

    // fetch user level data
    yield call(fetchUserLevelData);

    const datasetId = yield select(getDatasetId);
    if (datasetId) {
      yield put(createJobStatus({ type: 'SYNC', datasetId }));
      const { createJobStatusSuccessAction } = yield race({
        createJobStatusSuccessAction: take(createJobStatusSuccess),
        createJobStatusFailureAction: take(createJobStatusFailure),
      });

      // fetch data
      yield call(fetchData);

      // fetch featured data
      const featureFlagsLoadPending = yield select(featureFlagsSelectors.getLoadPending);
      if (!featureFlagsLoadPending) {
        yield call(fetchFeaturedData);
      } else {
        const featureFlagsIsLoading = yield select(featureFlagsSelectors.isLoading);
        if (!featureFlagsIsLoading) {
          yield call(initializeFeatureFlags);
        }
        yield fork(function* waitForFlags() {
          yield race([
            take(getFlagsSuccess),
            take(getFlagsFailure),
          ]);
          yield call(fetchFeaturedData);
        });
      }

      // trigger fi refresh
      let refreshStarted;
      if (action.payload?.context === 'init') {
        const lastRefreshTimeStr = localStorageEx.getItem(`${datasetId}_last_auto_refresh_time`);
        const lastRefreshTime = lastRefreshTimeStr ? parseInt(lastRefreshTimeStr, 10) : 0;
        const timeOkToAutoRefresh = lastRefreshTime + (60 * 60 * 1000);
        const now = Date.now();
        if (now >= timeOkToAutoRefresh) {
          // we are delaying just a bit so user doesn't get a Credential Required or MFA dialog immediately upon login
          yield put(setAwaitingToAutoRefresh(true));
          yield delay(5000);
          yield call(startRefreshAccounts, {autoRefresh: true});
          refreshStarted = true;
        }
      } else {
        yield call(startRefreshAccounts);
        refreshStarted = true;
      }
      if (refreshStarted) {
        const refreshAccountsCompletedAction = yield take(refreshAccountsCompleted);
        yield put(setAwaitingToAutoRefresh(false));
        if (refreshAccountsCompletedAction.payload.status === 'DONE_PROCESSING') {
          yield call(fetchRefreshedData);
        }
      }

      if (createJobStatusSuccessAction.payload?.id) {
        yield put(deleteJobStatus({ id: createJobStatusSuccessAction.payload?.id })); // it's a hack - refresh is complete, but fetch still is in progress
      }
    }
  }

  log.info('refresh (complete)<-');
}

function* fetchUserLevelData() {
  log.info('fetch data (user level)...');

  yield put(getSubscriptions());
  yield put(getEntitlements());
  yield put(getDatasets());
  yield put(getPreferences({section: 'shared', group: 'user'}));
}

function* fetchData() {
  log.info('fetch data (common)...');

  yield call(clearBalancesCache);

  yield put(getInstitutionLogins());
  yield put(getAccounts());
  yield put(getTags());
  yield put(getCategories());
  yield put(getDocuments());
  yield put(getTransactions());
  yield put(getPreferences({ section: 'shared', group: 'dataset' }));
}

function* fetchFeaturedData() {
  log.info('fetch data (featured)...');

  const featureFlags = yield select(featureFlagsSelectors.getFeatureFlags);
  if (featureFlags.get('investmentsAcme')) {
    yield put(getInvestmentBalancesV2());
    yield put(getInvestmentHoldingsV2());
  } else {
    yield put(getInvestmentAccounts());
    yield put(getInvestmentHoldings());
  }
  if (featureFlags.get('nps')) {
    yield put(checkIfNPSSurveyRequestedAction());
  }
  if (featureFlags.get('mcp')) {
    yield put(mcRulesExecute());
  }
  if (featureFlags.get('budgets')) {
    yield put(getBudgetsAction());
    yield put(getBudgetItemsAction());
    yield put(getCategoryGroupsAction());
    yield put(getCategoryGroupListsAction());
  }
  if (featureFlags.get('scheduledTransactions') || featureFlags.get('scheduledTransactionsInRegister')) {
    yield put(getScheduledTransactions());
  }
  if (featureFlags.get('spendingPlan')) {
    yield put(getFreeToSpend());
  }
  if (featureFlags.get('spendingWatchList')) {
    yield put(getFilters());
    yield put(getSpendingWatchListsInitial());
  }
  if (featureFlags.get('showMemorizedTransactionsSettings')) {
    yield put(getMemorizedRules());
  }
  if (featureFlags.get('showRenameRulesSettings')) {
    yield put(getRules());
  }
  if (featureFlags.get('inProductNotifications')) {
    yield put(getAlerts());
  }
}

function* startRefreshAccounts(payload) {
  log.info('accounts refresh triggered...');

  yield put(refreshAccounts(payload));

  const datasetId = yield select(getDatasetId);
  const now = Date.now();
  localStorageEx.setItem(`${datasetId}_last_auto_refresh_time`, now.toString());
};

// TODO: switch to web socket - and remove method below
function* fetchRefreshedData() {
  log.info('fetch data (refreshed)...');
  clearBalancesCache();

  yield put(getInstitutionLogins());
  yield put(getAccounts());
  yield put(getTransactions());

  const featureFlags = yield select(featureFlagsSelectors.getFeatureFlags);
  if (featureFlags.get('budgets')) {
    yield put(getBudgetItemsAction());
  }
  if (featureFlags.get('spendingPlan')) {
    yield put(getFreeToSpend());
  }
  if (featureFlags.get('spendingWatchList')) {
    yield put(getSpendingWatchLists());
  }
  if (featureFlags.get('investmentsAcme')) {
    yield put(getInvestmentBalancesV2());
    yield put(getInvestmentHoldingsV2());
  } else {
    yield put(getInvestmentAccounts());
    yield put(getInvestmentHoldings());
  }
  if (featureFlags.get('scheduledTransactions') || featureFlags.get('scheduledTransactionsInRegister')) {
    yield put(getScheduledTransactions());
  }
  if (featureFlags.get('showMemorizedTransactionsSettings')) {
    yield put(getMemorizedRules());
  }
  if (featureFlags.get('showRenameRulesSettings')) {
    yield put(getRules());
  }
  if (featureFlags.get('inProductNotifications')) {
    yield put(getAlerts());
  }
}

function* appRefreshAllActionWatcher() {
  yield takeLeading(actions.appRefreshAll, appRefreshAll);
}

export default [
  appRefreshAllActionWatcher,
];
